// Screen 6 — Protein & Carbohydrate Setup (wizard step 5 of 9)
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { WizardScreen } from '../../components/planner/WizardScreen';
import { FieldError } from '../../components/planner/FieldError';
import { useDraft, STEP_ROUTES } from '../../lib/plannerDraft';
import {
  caloriesFromMacros,
  macroDeviationWarning,
  deriveMacroTargets,
} from '../../lib/plannerCalc';

const PRESETS = [
  { id: 'auto', label: 'Set macros for me', blurb: 'Balanced targets based on your calories.' },
  { id: 'higher_protein', label: 'Higher protein', blurb: 'Prioritize protein at each meal.' },
  { id: 'lower_carb', label: 'Lower carb', blurb: 'Prioritize meals with fewer carbs.' },
  { id: 'custom', label: 'I know my macros', blurb: 'Enter grams for protein, carbs, fat.' },
];

const inputStyle = {
  padding: '12px 14px',
  border: '1.5px solid var(--line)',
  borderRadius: 'var(--radius)',
  background: '#fff',
  fontSize: '16px',
  fontFamily: 'Manrope, sans-serif',
  color: 'var(--ink)',
  outline: 'none',
};

export default function Step6Macros() {
  const nav = useNavigate();
  const { draft, patch, bump } = useDraft();
  const [showErr, setShowErr] = useState(false);

  const preset = draft.macro_preset;
  const custom = preset === 'custom';

  const derived = useMemo(() => {
    if (!preset || custom) return null;
    return deriveMacroTargets({
      preset,
      calorie_target: draft.calorie_target,
      weight_kg: draft.body?.normalized_weight_kg,
    });
  }, [preset, draft.calorie_target, draft.body?.normalized_weight_kg, custom]);

  const customVals = {
    protein_g: Number(draft.protein_g),
    carbs_g: Number(draft.carbs_g),
    fat_g: Number(draft.fat_g),
  };
  const customFilled = custom
    && [customVals.protein_g, customVals.carbs_g, customVals.fat_g].every((v) => Number.isFinite(v) && v >= 0 && v <= 500);
  const customEmpty = custom
    && ['protein_g','carbs_g','fat_g'].every((k) => String(draft[k]).trim() === '');
  const kcalFromMacros = caloriesFromMacros(customVals);
  const warning = custom && customFilled
    ? macroDeviationWarning({ calorie_target: draft.calorie_target, ...customVals })
    : null;

  const valid = preset && (!custom || customFilled);

  const onContinue = () => {
    if (!valid) { setShowErr(true); return; }
    // Persist the resolved macro targets so downstream screens have grams.
    if (!custom && derived) {
      patch({
        protein_g: derived.protein_g,
        carbs_g: derived.carbs_g,
        fat_g: derived.fat_g,
      });
    }
    bump(6);
    nav(STEP_ROUTES[6]);
  };

  return (
    <WizardScreen
      step={5}
      eyebrow="Macros"
      title="Set your macros"
      canContinue={valid}
      onContinue={onContinue}
    >
      <div role="radiogroup" aria-label="Macro preset" className="grid gap-3">
        {PRESETS.map((p) => {
          const selected = preset === p.id;
          return (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => patch({ macro_preset: p.id }, { recompute: true, invalidateGeneration: true })}
              className="p-4 text-left transition-colors"
              data-testid={`macro-preset-${p.id}-btn`}
              style={{
                background: selected ? 'rgba(239,104,32,0.08)' : '#fff',
                border: selected ? '2px solid var(--orange)' : '1.5px solid var(--line)',
                borderRadius: 'var(--radius)',
              }}
            >
              <div className="font-display uppercase" style={{ fontSize: '18px', letterSpacing: '0.06em' }}>
                {p.label}
              </div>
              <div className="mt-1 text-sm" style={{ color: 'var(--text-dim)' }}>{p.blurb}</div>
            </button>
          );
        })}
      </div>

      {derived && !custom && (
        <div className="mt-6 p-4" style={{ background: 'var(--cream-2)', borderRadius: 'var(--radius)' }} data-testid="macro-derived">
          <div className="eyebrow">Your targets</div>
          <div className="mt-2 text-sm">
            Protein <strong>{derived.protein_g}g</strong> · Carbs{' '}
            <strong>{derived.carbs_g}g</strong> · Fat <strong>{derived.fat_g}g</strong>
          </div>
        </div>
      )}

      {custom && (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { key: 'protein_g', label: 'Protein (g)' },
            { key: 'carbs_g', label: 'Carbs (g)' },
            { key: 'fat_g', label: 'Fat (g)' },
          ].map((f) => (
            <label key={f.key} className="block">
              <span className="font-display uppercase tracking-wider text-sm">{f.label}</span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                max={500}
                value={draft[f.key]}
                onChange={(e) => patch({ [f.key]: e.target.value }, { invalidateGeneration: true })}
                data-testid={`macro-custom-${f.key}-input`}
                className="mt-2 w-full"
                style={inputStyle}
              />
            </label>
          ))}
        </div>
      )}

      {custom && !customEmpty && (
        <div className="mt-4 p-3 text-sm" style={{ background: '#fff', border: '1px solid var(--line)', borderRadius: 'var(--radius)' }} data-testid="macro-custom-total">
          Total calories from macros: <strong>{Math.round(kcalFromMacros).toLocaleString()}</strong> cal
          {draft.calorie_target && (
            <> · target <strong>{draft.calorie_target.toLocaleString()}</strong> cal</>
          )}
        </div>
      )}

      {warning && (
        <div
          role="status"
          className="mt-3 p-3 text-sm"
          style={{
            background: 'var(--warning-bg)',
            border: '1px solid var(--warning)',
            borderRadius: 'var(--radius)',
            color: 'var(--warning)',
          }}
          data-testid="macro-warning"
        >
          Your macros come to {warning.kcal_from_macros.toLocaleString()} cal — {' '}
          {warning.pct}% {warning.direction} your target of {draft.calorie_target.toLocaleString()}.
          You can continue, but adjusting your macros will match your goal more closely.
        </div>
      )}

      <FieldError id="macro-error">
        {showErr && !valid
          ? custom
            ? 'Please enter protein, carbs, and fat (each 0–500 g).'
            : 'Please choose a macro preset.'
          : null}
      </FieldError>
    </WizardScreen>
  );
}
